import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { apiClient } from '../api/client';
import { MatchListItem } from '../types';
import { getBalanceQuality } from '../utils/calculations';

export function MatchHistory() {
  const [matches, setMatches] = useState<MatchListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [total, setTotal] = useState(0);

  useEffect(() => {
    loadMatches();
  }, []);

  const loadMatches = async () => {
    try {
      setError('');
      setLoading(true);
      const response = await apiClient.getMatches({ limit: 50, offset: 0 });
      setMatches(response.matches);
      setTotal(response.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load matches');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this match?')) return;

    try {
      await apiClient.deleteMatch(id);
      await loadMatches();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete match');
    }
  };

  const handleCopyTeams = async (matchId: string) => {
    try {
      const response = await apiClient.getMatch(matchId);
      const match = response.match;

      const teamANames = match.teams.team_a.map((p: any) => p.name).join('\n');
      const teamBNames = match.teams.team_b.map((p: any) => p.name).join('\n');

      const teamsText = `🔵 Team A:\n${teamANames}\n\n🔴 Team B:\n${teamBNames}`;

      await navigator.clipboard.writeText(teamsText);
      alert('Teams copied to clipboard!');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to copy teams');
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Calculate algorithm breakdown
  const algorithmBreakdown = {
    v1: matches.filter(m => m.generation_method === 'AUTO' && m.generation_model === 'v1').length,
    v2: matches.filter(m => m.generation_method === 'AUTO' && m.generation_model === 'v2').length,
    manual: matches.filter(m => m.generation_method === 'MANUAL').length,
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">Match History</h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              View and manage your past matches
            </p>
          </div>
          <Link to="/generate" className="w-full sm:w-auto">
            <Button className="w-full">Generate New Teams</Button>
          </Link>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Algorithm Breakdown Summary */}
        {!loading && matches.length > 0 && (algorithmBreakdown.v1 > 0 || algorithmBreakdown.v2 > 0) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card className="bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-blue-900 dark:text-blue-300">📊 Algorithm V1</div>
                  <div className="text-xs text-blue-700 dark:text-blue-400 mt-1">Position Ratings</div>
                </div>
                <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">{algorithmBreakdown.v1}</div>
              </div>
              <div className="text-xs text-blue-600 dark:text-blue-500 mt-2">
                Uses FWD/MID/DEF/GK ratings
              </div>
            </Card>

            <Card className="bg-purple-50 dark:bg-purple-950 border-purple-200 dark:border-purple-800">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-purple-900 dark:text-purple-300">⚡ Algorithm V2</div>
                  <div className="text-xs text-purple-700 dark:text-purple-400 mt-1">Raw Attributes</div>
                </div>
                <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">{algorithmBreakdown.v2}</div>
              </div>
              <div className="text-xs text-purple-600 dark:text-purple-500 mt-2">
                Technical, Physical, Mental
              </div>
            </Card>

            <Card className="bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-300">✋ Manual</div>
                  <div className="text-xs text-gray-700 dark:text-gray-400 mt-1">Hand-picked Teams</div>
                </div>
                <div className="text-3xl font-bold text-gray-600 dark:text-gray-400">{algorithmBreakdown.manual}</div>
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-500 mt-2">
                User-selected teams
              </div>
            </Card>
          </div>
        )}

        {/* Matches List */}
        <Card>
          {loading ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">Loading matches...</div>
          ) : matches.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-500 dark:text-gray-400 mb-4">No matches yet</div>
              <Link to="/generate">
                <Button>Generate Your First Teams</Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {total} {total === 1 ? 'match' : 'matches'} total
              </div>

              <div className="space-y-3">
                {matches.map((match) => {
                  const balanceInfo = getBalanceQuality(match.rating_difference);
                  return (
                    <div
                      key={match.id}
                      className="flex flex-col md:flex-row md:items-center md:justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors gap-4"
                    >
                      <div className="flex-1">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-2 sm:space-y-0">
                          <div>
                            <div className="font-medium text-gray-900 dark:text-gray-100">
                              {formatDate(match.date)}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                <span>
                                  {match.team_size}v{match.team_size}
                                </span>
                                <span className="hidden sm:inline">•</span>
                                <span>
                                  {match.generation_method === 'AUTO' ? 'Auto Generated' : 'Manual'}
                                </span>
                                {match.generation_method === 'AUTO' && match.generation_model && (
                                  <>
                                    <span>•</span>
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                      match.generation_model === 'v2' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                                      match.generation_model === 'v3' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                                      match.generation_model === 'super' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                                      match.generation_model === 'v4-beta' ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200' :
                                      match.generation_model === 'super-beta' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                                      'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                    }`}>
                                      {match.generation_model === 'v1' && '📊 V1'}
                                      {match.generation_model === 'v2' && '⚡ V2'}
                                      {match.generation_model === 'v3' && '🧬 V3'}
                                      {match.generation_model === 'super' && '⚡ Super'}
                                      {match.generation_model === 'v4-beta' && '🎯 V4 BETA'}
                                      {match.generation_model === 'super-beta' && '⚡ Super BETA'}
                                    </span>
                                  </>
                                )}
                              </div>
                              {match.generation_method === 'AUTO' && match.generation_model && (
                                <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                  {match.generation_model === 'v1' && 'Position ratings (FWD/MID/DEF/GK)'}
                                  {match.generation_model === 'v2' && 'Raw attributes (Technical/Physical/Mental)'}
                                  {match.generation_model === 'v3' && 'Chemistry & learning from match history'}
                                  {match.generation_model === 'super' && 'Multi-dimensional (5 factors)'}
                                  {match.generation_model === 'v4-beta' && 'Position slider (0-100 preference)'}
                                  {match.generation_model === 'super-beta' && 'Ultimate: Super + Position Slider'}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className={`text-sm font-medium ${balanceInfo.color}`}>
                            {balanceInfo.emoji} {balanceInfo.quality}
                          </div>

                          {match.variety_score !== undefined && (
                            <div className={`text-sm font-medium ${
                              match.variety_score >= 8 ? 'text-green-600' :
                              match.variety_score >= 6 ? 'text-yellow-600' :
                              match.variety_score >= 4 ? 'text-orange-600' : 'text-red-600'
                            }`}>
                              {match.variety_score >= 8 ? '🟢' :
                               match.variety_score >= 6 ? '🟡' :
                               match.variety_score >= 4 ? '🟠' : '🔴'}
                              {' '}Variety: {match.variety_label}
                            </div>
                          )}

                          {match.score_team_a !== null && match.score_team_b !== null && (
                            <div className="flex items-center space-x-2">
                              <div className="text-sm font-medium bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200 px-3 py-1 rounded">
                                Team A: {match.score_team_a}
                              </div>
                              <div className="text-sm font-medium bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200 px-3 py-1 rounded">
                                Team B: {match.score_team_b}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full md:w-auto">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleCopyTeams(match.id)}
                          className="w-full sm:w-auto"
                        >
                          📋 Copy
                        </Button>
                        <Link to={`/matches/${match.id}`} className="w-full sm:w-auto">
                          <Button variant="secondary" size="sm" className="w-full">
                            View
                          </Button>
                        </Link>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(match.id)}
                          className="w-full sm:w-auto"
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
}
